import { getCalling, getTown, SKILLS } from '@wyn/content';
import { type MeResponse, TERMS_VERSION } from '@wyn/contracts';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { UI } from '../icons/ui-icons.ts';
import { identifyPlayer } from '../lib/analytics.ts';
import { CookieChoices } from './Consent.tsx';
import { Icon } from './Icon.tsx';
import { useDevClock, useDevGrant } from '../lib/queries.ts';
import { rt } from '../lib/runtime.ts';
import { coin, formatDuration, useNow } from '../lib/time.ts';
import { BarMeter } from './BarMeter.tsx';
import { NoticeSummary } from './NoticeBoard.tsx';
import { ChooseName } from './Rename.tsx';
import { Banished } from './Sanction.tsx';
import { SocketBadge } from './Realtime.tsx';
import { TermsChanged } from './Terms.tsx';

/** game-icons.net is CC BY 3.0: the credit has to be visible wherever the icons are. */
export function IconCredit() {
  return (
    <span className="ml-auto">
      Icons by <a className="underline hover:text-ink" href="https://game-icons.net" target="_blank" rel="noreferrer">game-icons.net</a>, CC BY 3.0
    </span>
  );
}

/** The rules, the terms and the privacy notice: linked from every footer, signed in or out. */
export function LegalLinks() {
  return (
    <span className="flex gap-3">
      <Link to="/rules" className="underline hover:text-ink">Rules</Link>
      <Link to="/terms" className="underline hover:text-ink">Terms</Link>
      <Link to="/privacy" className="underline hover:text-ink">Privacy</Link>
      <CookieChoices />
    </span>
  );
}

export function Masthead() {
  return (
    <Link to="/" className="flex items-baseline gap-2 text-gilt">
      <Icon name={UI.brand} className="self-center text-2xl" />
      <span className="smallcaps text-xl tracking-widest">Wynchester</span>
    </Link>
  );
}

const statusText = (p: NonNullable<MeResponse['player']>, serverNow: number) => {
  const s = p.status;
  switch (s.kind) {
    case 'idle': return null;
    case 'infirmary': return `In the Infirmary · ${formatDuration(s.endsAt - serverNow)}`;
    case 'gaol': return `In gaol · ${formatDuration(s.endsAt - serverNow)}`;
    case 'travelling': return `On the road to ${getTown(s.to).name} · ${formatDuration(s.endsAt - serverNow)}`;
  }
};

export function GameShell({ me, skew, children }: { me: MeResponse; skew: number; children: ReactNode }) {
  const now = useNow();
  const p = me.player!;
  const town = getTown(p.town);
  const status = statusText(p, now + skew);
  const endsAt = p.status.kind === 'idle' ? null : p.status.endsAt;
  const qc = useQueryClient();
  const sanctionEnds = p.sanction?.until ?? null;
  const boardEnds = p.board?.endsAt ?? null;
  const due = (endsAt !== null && now + skew >= endsAt) || (sanctionEnds !== null && now + skew >= sanctionEnds) || (boardEnds !== null && now + skew >= boardEnds);
  // When a timer runs out on screen (or the day, and with it the notice board), ask the server what happened rather than waiting for the next poll.
  useEffect(() => {
    if (due) void qc.invalidateQueries();
  }, [due, qc]);
  // Ties this browser's visits to the player only if their analytics setting allows it.
  useEffect(() => {
    identifyPlayer({ id: p.id, analytics: p.analytics, calling: p.calling, homeTown: p.homeTown });
  }, [p.id, p.analytics, p.calling, p.homeTown]);
  if (p.sanction?.kind === 'ban') return <Banished sanction={p.sanction} />;
  if (me.termsVersion !== TERMS_VERSION || p.born === null) return <TermsChanged firstTime={me.termsVersion === null} askBorn={p.born === null} />;
  if (p.renameRequired) return <ChooseName oldName={p.name} reason={p.renameRequired.reason} />;
  return (
    <div className="mx-auto flex min-h-dvh max-w-6xl flex-col px-4">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule py-3">
        <Masthead />
        <nav className="flex flex-wrap items-center gap-4 text-sm">
          <Link to="/town" className="nav-link">
            {p.status.kind === 'travelling' ? <><Icon name={UI.road} className="mr-1" />The road</> : <><Icon name={town.icon} className="mr-1" />{town.name}</>}
          </Link>
          {p.fight && p.fight.expiresAt > now + skew && (
            <Link to="/fight" className="text-blood hover:text-ink [&.active]:text-gilt"><Icon name={UI.fight} className="mr-1" />Fight</Link>
          )}
          <Link to="/character" className="nav-link"><Icon name={UI.character} className="mr-1" />Character</Link>
          <Link to="/market" className="nav-link"><Icon name={UI.market} className="mr-1" />Market</Link>
          <Link to="/trades" className="nav-link"><Icon name={UI.trades} className="mr-1" />Trades</Link>
          <Link to="/chat" className="nav-link"><Icon name={UI.talk} className="mr-1" />Talk</Link>
          <Link to="/chronicle" className="nav-link"><Icon name={UI.chronicle} className="mr-1" />Chronicle</Link>
          <Link to="/leaderboards" className="nav-link"><Icon name={UI.boards} className="mr-1" />Leaderboards</Link>
          {me.isAdmin && <Link to="/admin" className="nav-link"><Icon name={UI.stewards} className="mr-1" />Stewards</Link>}
          <button type="button" className="text-ink-faint hover:text-ink" onClick={() => rt().auth.signOut()}>
            Sign out
          </button>
        </nav>
      </header>
      <div className="grid flex-1 gap-6 py-6 md:grid-cols-[16rem_1fr]">
        <aside className="space-y-4">
          <div className="panel space-y-1">
            <div className="text-lg">{p.name}</div>
            {p.title && <div className="text-sm text-gilt">{p.title}</div>}
            <div className="text-sm text-ink-dim">Level {p.level} · {getCalling(p.calling).name}</div>
            <div className="font-mono text-sm text-gilt">{coin(p.purse)}</div>
            {p.bank + p.locked > 0 && <div className="font-mono text-xs text-ink-faint">bank {coin(p.bank)}{p.locked > 0 && ` · locked ${coin(p.locked)}`}</div>}
            {status && <div className="pt-1 text-sm text-ochre">{status}</div>}
            {p.protection && (
              <div className="pt-1 text-xs text-ink-faint">
                Under the Crown’s protection{p.protection.level ? ' until level 5' : ` for ${formatDuration(p.protection.until - (now + skew))}`}
              </div>
            )}
          </div>
          <div className="panel space-y-3">
            {(['life', 'vigour', 'nerve', 'focus', 'mood'] as const).map((b) => (
              <BarMeter key={b} name={b} bar={p.bars[b]} now={now} skew={skew} />
            ))}
          </div>
          <NoticeSummary player={p} />
        </aside>
        <main className="min-w-0">{children}</main>
      </div>
      <footer className="flex flex-wrap items-center gap-3 border-t border-rule py-3 text-xs text-ink-faint">
        <span>Game time is UTC · {rt().config.stage}</span>
        <SocketBadge />
        {rt().config.stage === 'local' && <DevClock skew={skew} />}
        <LegalLinks />
        <IconCredit />
      </footer>
    </div>
  );
}

/** Renown from level 1 to level 5 (60, 224, 484, 838), enough to leave the Crown's protection. */
const LEVEL_5_RENOWN = 1606;

/** Every skill at 25 makes each Discipline 100 levels: the first title in all four. */
const SKILL_UP = Object.fromEntries(SKILLS.map((s) => [s, 25]));

/** From a server time to a minute past the next UTC midnight: a new day, and a new notice board. */
const untilTomorrow = (serverNow: number) => Math.ceil((serverNow + 1) / 86_400_000) * 86_400_000 + 60_000 - serverNow;

/** Local dev only: the API exposes /v1/dev/clock so nobody has to wait out a real road, and /v1/dev/grant to grow up. */
function DevClock({ skew }: { skew: number }) {
  const m = useDevClock();
  const grow = useDevGrant();
  return (
    <span className="flex items-center gap-1">
      <span>dev clock:</span>
      {[5, 30].map((min) => (
        <button key={min} type="button" className="btn-quiet py-0" disabled={m.isPending} onClick={() => m.mutate(min * 60_000)}>
          +{min}m
        </button>
      ))}
      <button type="button" className="btn-quiet py-0" disabled={m.isPending} onClick={() => m.mutate(untilTomorrow(Date.now() + skew))}>
        next day
      </button>
      <button type="button" className="btn-quiet py-0" disabled={grow.isPending} onClick={() => grow.mutate({ renown: LEVEL_5_RENOWN, ageHours: 73 })}>
        grow up
      </button>
      <button type="button" className="btn-quiet py-0" disabled={grow.isPending} onClick={() => grow.mutate({ renown: 0, skills: SKILL_UP })}>
        skill up
      </button>
    </span>
  );
}
